import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { AppShell } from './components/shell';
import { TrustXThemeProvider } from './theme';
import Overview from './components/Overview';
import Profile from './components/Profile';
import Integrations from './components/Integrations';
import LoginButton from './components/LoginButton';
import { UserMetadataProvider, useUserMetadata } from './context/UserMetadataContext';
import { ApolloProvider } from './graphql/ApolloProvider';
// Neo4jVisualizer removed - using GraphRAG instead

// Profile360 Components
// import RiskPostureDashboard from './components/Profile360/components/RiskPostureDashboard';  // TODO: Re-enable when used
import PrivilegeCreepDashboard from './components/Profile360/components/PrivilegeCreepDashboard';
import ExcessiveAccessDashboard from './components/Profile360/components/ExcessiveAccessDashboard';
// import TrustDegradationDashboard from './components/Profile360/components/TrustDegradationDashboard';  // TODO: Re-enable when used
import SuspiciousActivityDashboard from './components/Profile360/components/SuspiciousActivityDashboard';
import DormantUsersCard from './components/Profile360/components/DormantUsersCard';
import UsersListComponent from './components/Profile360/components/UsersListComponent';
import { GraphRAGDashboard } from './components/Profile360/components/graphrag';
import AccessReviewPage from './components/Profile360/components/review/AccessReviewPage';
import RiskScenariosPage from './components/Profile360/components/risk-scenarios/RiskScenariosPage';
import { AnalyticsDashboard } from './components/Profile360';

// Page Wrappers (Pillar-based pages)
import { RiskPosturePage, TrustIntelligencePage, EvidencePage } from './pages';

import 'react-toastify/dist/ReactToastify.css';

// TenantGuard: Blocks entire app if tenant validation fails
function TenantGuard({ children }) {
  const { error, isLoading } = useUserMetadata();

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h4" color="error.main" gutterBottom>Access Denied</Typography>
          <Typography color="text.secondary">{error}</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            Please contact your administrator to be assigned to an active organization.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <Typography color="text.secondary">Verifying tenant access...</Typography>
      </Box>
    );
  }

  return children;
}

// Wrapper to inject tenant context
function WithTenantContext({ Component }) {
  const { userMetadata } = useUserMetadata();
  return <Component tenantName={userMetadata?.company} />;
}

// Legacy Analytics Dashboard Wrapper
function AnalyticsDashboardWrapper() {
  const { userMetadata } = useUserMetadata();
  return <AnalyticsDashboard tenantName={userMetadata?.company} />;
}

function App() {
  const { isAuthenticated, isLoading, error } = useAuth0();

  if (error) {
    return (
      <TrustXThemeProvider>
        <Router>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h4" color="error.main" gutterBottom>Authentication Error</Typography>
              <Typography color="text.secondary">{error.message}</Typography>
              <Box sx={{ mt: 2 }}><LoginButton /></Box>
            </Box>
          </Box>
        </Router>
      </TrustXThemeProvider>
    );
  }

  if (isLoading) {
    return (
      <TrustXThemeProvider>
        <Router>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </Router>
      </TrustXThemeProvider>
    );
  }

  return (
    <TrustXThemeProvider>
      <Router>
        {!isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h3" gutterBottom>Welcome to {process.env.REACT_APP_NAME}</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>Please log in to access your dashboard.</Typography>
              <LoginButton />
            </Box>
          </Box>
        ) : (
          <UserMetadataProvider>
            <ApolloProvider>
              <TenantGuard>
                <AppShell>
                  <Routes>
                    {/* Overview */}
                    <Route path="/overview" element={<Overview />} />

                    {/* Pillar 1: Risk Posture (IARPM) */}
                    <Route path="/risk-posture" element={<RiskPosturePage />} />
                    <Route path="/risk-posture/privilege-creep" element={<WithTenantContext Component={PrivilegeCreepDashboard} />} />
                    <Route path="/risk-posture/excessive-access" element={<WithTenantContext Component={ExcessiveAccessDashboard} />} />
                    <Route path="/risk-posture/dormant-users" element={<WithTenantContext Component={DormantUsersCard} />} />
                    <Route path="/risk-posture/users" element={<WithTenantContext Component={UsersListComponent} />} />

                    {/* Pillar 3: Trust Intelligence */}
                    <Route path="/trust-intelligence" element={<TrustIntelligencePage />} />
                    <Route path="/trust-intelligence/suspicious-activity" element={<WithTenantContext Component={SuspiciousActivityDashboard} />} />
                    <Route path="/trust-intelligence/velocity" element={<TrustIntelligencePage section="velocity" />} />

                    {/* Pillar 2: Evidence & Compliance */}
                    <Route path="/evidence" element={<EvidencePage />} />
                    <Route path="/evidence/timeline" element={<EvidencePage section="timeline" />} />
                    <Route path="/evidence/export" element={<EvidencePage section="export" />} />

                    {/* Knowledge Graph - GraphRAG only (Neo4j Explorer removed) */}
                    <Route path="/knowledge-graph" element={<Navigate to="/knowledge-graph/graphrag" replace />} />
                    <Route path="/knowledge-graph/graphrag" element={<GraphRAGDashboard />} />
                    <Route path="/access-review" element={<AccessReviewPage />} />
                    <Route path="/risk-scenarios" element={<RiskScenariosPage />} />
                    {/* Integrations */}
                    <Route path="/integrations" element={<Integrations />} />
                    <Route path="/profile" element={<Profile />} />

                    {/* Legacy routes (redirect to new structure) */}
                    <Route path="/Access" element={<Navigate to="/knowledge-graph/graphrag" replace />} />
                    <Route path="/profile360/analytics" element={<AnalyticsDashboardWrapper />} />
                    <Route path="/profile360/privilege-creep" element={<Navigate to="/risk-posture/privilege-creep" replace />} />
                    <Route path="/profile360/graphrag" element={<Navigate to="/knowledge-graph/graphrag" replace />} />

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/overview" replace />} />
                  </Routes>
                </AppShell>
              </TenantGuard>
            </ApolloProvider>
          </UserMetadataProvider>
        )}
      </Router>
    </TrustXThemeProvider>
  );
}

export default App;
